import api from '@/axios';

// Los endpoints de listado del backend devuelven un objeto paginado
// ({ items, page, pageSize, totalItems, totalPages, hasPrevious, hasNext }) con 20 filas por
// defecto y un máximo de 100 por página. Ver
// specs/004-fix-listados-paginados/contracts/listados-paginados.md.

export const MAX_PAGE_SIZE = 100;
const MAX_PAGES = 50;
const CONCURRENCY = 4;

// Acepta el formato paginado o un arreglo plano y devuelve siempre el arreglo de filas.
export function extractItems(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

// Pide la página `page` (de `pageSize` filas) y devuelve la respuesta ya normalizada.
export async function fetchPage(endpoint, { page = 1, pageSize = MAX_PAGE_SIZE, params = {}, signal } = {}) {
  const response = await api.get(endpoint, { params: { ...params, page, pageSize }, signal });
  const data = response.data;
  const items = extractItems(data);
  const paged = data && !Array.isArray(data) && Array.isArray(data.items);
  return {
    items,
    page: paged ? data.page ?? page : 1,
    totalItems: paged ? data.totalItems ?? items.length : items.length,
    totalPages: paged ? data.totalPages ?? 1 : 1,
  };
}

// Lee todas las páginas de un listado (de 100 en 100; la primera define `totalPages` y el
// resto se pide en paralelo con concurrencia acotada). Devuelve las filas en orden.
// Si el listado supera MAX_PAGES páginas se detiene y marca `truncado`.
// `options.first` permite reutilizar una página 1 ya pedida (evita repetirla) y
// `options.signal` cancelar las peticiones.
export async function fetchAllItems(endpoint, params = {}, { first: prefetched, signal } = {}) {
  const first = prefetched || (await fetchPage(endpoint, { page: 1, params, signal }));
  const totalPages = Math.min(first.totalPages, MAX_PAGES);
  const truncado = first.totalPages > MAX_PAGES;
  if (totalPages <= 1) return { items: first.items, totalItems: first.totalItems, truncado };

  const pages = new Array(totalPages);
  pages[0] = first.items;
  const pending = [];
  for (let n = 2; n <= totalPages; n += 1) pending.push(n);

  const worker = async () => {
    while (pending.length) {
      const n = pending.shift();
      pages[n - 1] = (await fetchPage(endpoint, { page: n, params, signal })).items;
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker));

  return { items: pages.flat(), totalItems: first.totalItems, truncado };
}
